import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Megaphone } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

/* Two-tap confirm button: first click arms, second click executes. */
export function ConfirmButton({ label, confirmLabel, onConfirm, testId, variant = "dark" }) {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 3500);
    return () => clearTimeout(t);
  }, [armed]);

  const base = "rounded-lg px-3 py-1.5 text-xs font-bold transition-colors disabled:opacity-60";
  const tones = {
    dark: armed ? "bg-red-600 text-white" : "bg-charcoal text-cream hover:bg-charcoal-hover",
    danger: armed ? "bg-red-600 text-white" : "bg-red-100 text-red-700 hover:bg-red-200",
    success: armed ? "bg-emerald-600 text-white" : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200",
    warn: armed ? "bg-amber-600 text-white" : "bg-amber-100 text-amber-700 hover:bg-amber-200",
  };
  return (
    <button
      data-testid={testId}
      disabled={busy}
      className={`${base} ${tones[variant]}`}
      onClick={async () => {
        if (!armed) { setArmed(true); return; }
        setArmed(false);
        setBusy(true);
        try { await onConfirm(); } finally { setBusy(false); }
      }}
    >
      {busy ? "Working…" : armed ? confirmLabel : label}
    </button>
  );
}

export async function runAction(action, target_id, payload) {
  const { data } = await api.post("/app-admin/action", { action, target_id, payload });
  return data;
}

export function BroadcastDialog({ open, onOpenChange, onSent }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("all");
  const [channel, setChannel] = useState("inapp");
  const [signature, setSignature] = useState("— The Dishly Team");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!title.trim() || !body.trim()) { toast.error("Title and message are required"); return; }
    setSending(true);
    try {
      const res = await runAction("broadcast", null, { title: title.trim(), body: body.trim(), audience, channel, signature: signature.trim() });
      toast.success(`Broadcast sent${res.recipient_count != null ? ` to ${res.recipient_count} recipients` : ""}`);
      setTitle(""); setBody("");
      onOpenChange(false);
      onSent();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to send broadcast");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="broadcast-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Megaphone className="h-4 w-4 text-terracotta" /> New broadcast</DialogTitle>
          <DialogDescription>Sends immediately to everyone in the selected audience on the live app.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-[13px] font-semibold">Title</Label>
            <Input data-testid="broadcast-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Weekend pickup hours" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[13px] font-semibold">Message</Label>
            <Textarea data-testid="broadcast-body" value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="Write your announcement…" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-[13px] font-semibold">Audience</Label>
              <Select value={audience} onValueChange={setAudience}>
                <SelectTrigger data-testid="broadcast-audience"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="audience-all">Everyone</SelectItem>
                  <SelectItem value="customers" data-testid="audience-customers">Customers</SelectItem>
                  <SelectItem value="vendors" data-testid="audience-vendors">Creators</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[13px] font-semibold">Channel</Label>
              <Select value={channel} onValueChange={setChannel}>
                <SelectTrigger data-testid="broadcast-channel"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="inapp" data-testid="channel-inapp">In-app</SelectItem>
                  <SelectItem value="email" data-testid="channel-email">Email</SelectItem>
                  <SelectItem value="both" data-testid="channel-both">Both</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[13px] font-semibold">Signature</Label>
            <Input data-testid="broadcast-signature" value={signature} onChange={(e) => setSignature(e.target.value)} />
          </div>
          <ConfirmButton
            label="Send broadcast"
            confirmLabel="Tap again to send"
            onConfirm={send}
            testId="broadcast-send-btn"
            variant="dark"
          />
          {sending && <p className="text-xs text-charcoal/50">Sending…</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const DETAIL_FIELDS = [
  ["Name", "name"], ["Email", "email"], ["Role", "role"], ["City", "city"], ["Area", "area"],
  ["Rating", "rating"], ["Reviews", "review_count"], ["Suspended", "suspended"],
  ["Approval", "approval_status"], ["Email verified", "email_verified"],
  ["Accepting orders", "accepting_orders"], ["Response time (min)", "response_mins"],
  ["Joined", "created_at"],
];

export function UserDialog({ userId, onOpenChange, onAction }) {
  const [user, setUser] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!userId) { setUser(null); return; }
    setUser(null); setError("");
    api.get(`/app-admin/detail/user/${userId}`)
      .then(({ data }) => setUser(data))
      .catch((err) => setError(formatApiError(err.response?.data?.detail) || "Could not load user"));
  }, [userId]);

  const fmtVal = (k, v) => {
    if (v == null || v === "") return "—";
    if (typeof v === "boolean") return v ? "Yes" : "No";
    if (k === "created_at") return new Date(v).toLocaleString("en-US", { month: "numeric", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
    return String(v);
  };

  return (
    <Dialog open={!!userId} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="user-dialog">
        <DialogHeader>
          <DialogTitle>{user ? user.name : "User details"}</DialogTitle>
          <DialogDescription>Live from the Dishly app.</DialogDescription>
        </DialogHeader>
        {error && <p className="text-sm text-red-600" data-testid="user-dialog-error">{error}</p>}
        {!user && !error && <p className="text-sm text-charcoal/50">Loading…</p>}
        {user && (
          <div className="space-y-4">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              {DETAIL_FIELDS.map(([label, key]) => (
                <div key={key}>
                  <dt className="text-[11px] font-bold uppercase tracking-wide text-charcoal/40">{label}</dt>
                  <dd className="font-semibold text-charcoal" data-testid={`user-detail-${key}`}>{fmtVal(key, user[key])}</dd>
                </div>
              ))}
            </dl>
            <div className="flex gap-2 border-t border-black/5 pt-4">
              {user.suspended ? (
                <ConfirmButton label="Reactivate account" confirmLabel="Confirm reactivate" variant="success" testId="user-reactivate-btn"
                  onConfirm={async () => { await runAction("user_status", user.user_id, { action: "reactivate" }); toast.success("Account reactivated"); onAction(); }} />
              ) : (
                <ConfirmButton label="Suspend account" confirmLabel="Confirm suspend" variant="danger" testId="user-suspend-btn"
                  onConfirm={async () => { await runAction("user_status", user.user_id, { action: "suspend" }); toast.success("Account suspended"); onAction(); }} />
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
